import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Plus } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { trackFeatureUsed } from "@/lib/analytics";
import type { Database } from "@/integrations/supabase/types";

type JobStatus = Database["public"]["Enums"]["job_status"];

interface Person {
  id: string;
  name: string;
}

interface AddJobDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onJobCreated?: () => void;
}

export function AddJobDialog({ open, onOpenChange, onJobCreated }: AddJobDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);

  // Form fields
  const [jobName, setJobName] = useState("");
  const [jobNumber, setJobNumber] = useState("");
  const [status, setStatus] = useState<JobStatus>("Planning");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [address, setAddress] = useState("");
  const [pmId, setPmId] = useState("unassigned");
  const [fieldLeadId, setFieldLeadId] = useState("unassigned");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [projectedEndDate, setProjectedEndDate] = useState<Date | undefined>();
  const [doorCode, setDoorCode] = useState("");
  const [garageCode, setGarageCode] = useState("");
  const [notes, setNotes] = useState("");

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setIsOpen = isControlled ? onOpenChange! : setInternalOpen;

  useEffect(() => {
    fetchPeople();
  }, []);

  const fetchPeople = async () => {
    const { data, error } = await supabase
      .from("people")
      .select("id, name")
      .eq("is_active", true)
      .order("name");

    if (error) {
      console.error("Error fetching people:", error);
      return;
    }
    setPeople(data || []);
  };

  const resetForm = () => {
    setJobName("");
    setJobNumber("");
    setStatus("Planning");
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setAddress("");
    setPmId("unassigned");
    setFieldLeadId("unassigned");
    setStartDate(undefined);
    setProjectedEndDate(undefined);
    setDoorCode("");
    setGarageCode("");
    setNotes("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!jobName.trim()) {
      toast({ title: "Job Name is required", variant: "destructive" });
      return;
    }
    if (!clientName.trim()) {
      toast({ title: "Client Name is required", variant: "destructive" });
      return;
    }
    if (!address.trim()) {
      toast({ title: "Address is required", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);

    const jobData = {
      name: jobName.trim(),
      job_number: jobNumber.trim() || null,
      status,
      client_name: clientName.trim(),
      client_email: clientEmail.trim() || null,
      client_phone: clientPhone.trim() || null,
      address: address.trim(),
      pm_id: pmId === "unassigned" ? null : pmId,
      field_lead_id: fieldLeadId === "unassigned" ? null : fieldLeadId,
      start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
      projected_end_date: projectedEndDate ? format(projectedEndDate, "yyyy-MM-dd") : null,
      door_code: doorCode.trim() || null,
      garage_code: garageCode.trim() || null,
      notes: notes.trim() || null,
    };

    const { error } = await supabase.from("jobs").insert(jobData);

    setIsSubmitting(false);

    if (error) {
      console.error("Error creating job:", error);
      toast({
        title: "Failed to create job",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({ title: "Job created successfully" });
    trackFeatureUsed('job_creation', { status, hasSchedule: !!startDate });
    resetForm();
    setIsOpen(false);
    onJobCreated?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button
            size="icon"
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] h-14 w-14 rounded-full shadow-lg bg-success hover:bg-success/90 text-white"
          >
            <Plus className="h-6 w-6" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Job</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Basic Info</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="jobName">Job Name *</Label>
                <Input
                  id="jobName"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  placeholder="Enter job name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobNumber">Job Number</Label>
                <Input
                  id="jobNumber"
                  value={jobNumber}
                  onChange={(e) => setJobNumber(e.target.value)}
                  placeholder="e.g., JOB-2024-001"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as JobStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Blocked">Stalled</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Client Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Client Info</h3>
            <div className="space-y-2">
              <Label htmlFor="clientName">Client Name *</Label>
              <Input
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="clientEmail">Client Email</Label>
                <Input
                  id="clientEmail"
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientPhone">Client Phone</Label>
                <Input
                  id="clientPhone"
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Location</h3>
            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter job site address"
                required
              />
            </div>
          </div>

          {/* Team Assignment */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Team Assignment</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pm">Project Manager</Label>
                <Select value={pmId} onValueChange={setPmId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select PM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {people.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fieldLead">Field Lead</Label>
                <Select value={fieldLeadId} onValueChange={setFieldLeadId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Field Lead" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {people.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Schedule</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Projected End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !projectedEndDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {projectedEndDate ? format(projectedEndDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={projectedEndDate}
                      onSelect={setProjectedEndDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Site Access */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Site Access</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="doorCode">Door Code</Label>
                <Input
                  id="doorCode"
                  value={doorCode}
                  onChange={(e) => setDoorCode(e.target.value)}
                  placeholder="Enter door code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="garageCode">Garage Code</Label>
                <Input
                  id="garageCode"
                  value={garageCode}
                  onChange={(e) => setGarageCode(e.target.value)}
                  placeholder="Enter garage code"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Notes</h3>
            <div className="space-y-2">
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes about the job..."
                rows={3}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Job"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
